import { Component, OnInit, QueryList, ViewChild, ViewChildren, ViewContainerRef } from '@angular/core';
import { HttpClient, HttpClientModule } from '@angular/common/http';
import { CommonModule, PlatformLocation } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { WidgetRendererComponent } from '../widget-renderer/widget-renderer.component';
import { ChartRendererService } from '../../../../my-shared-lib/src/lib/shared-services/chart-renderer.service';
import { ActivatedRoute, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { ConfigService } from '../../../../my-shared-lib/src/lib/shared-services/config.service';
import { GlobalFilterComponent } from '../../../../my-shared-lib/src/lib/global-components/global-filter/global-filter.component';
import { Subscription } from 'rxjs';
import { GlobalFilterService } from '../../../../my-shared-lib/src/lib/shared-services/global-filter-service';
import { environment } from '../../environments/environment';
import { PlatformService } from '../../../../my-shared-lib/src/lib/shared-services/platform.service.service';

interface DashboardNavItem {
  label: string;
  route: string;
}

interface SideNavConfigItem {
  name?: string;
  path?: string;
  children?: SideNavConfigItem[];
}

@Component({
  selector: 'app-dashboard',
  imports: [
    HttpClientModule,
    WidgetRendererComponent,
    CommonModule,
    GlobalFilterComponent,
    FormsModule,
    RouterLink,
    RouterLinkActive,
  ],
  standalone: true,
  templateUrl: './dashboard.component.html',
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit {
  configurations: any = {};
  dashboardType: any;
  @ViewChildren('chartContainer', { read: ViewContainerRef })
  chartContainers!: QueryList<ViewContainerRef>;
  private subscriptions = new Subscription();

  globalDropdownConfig: any;
  selectedYear: number = 2024;
  configFileName!: string;

  showEditDialog: boolean = false;
  editRecordData: any = null;
  editConfig: any = null;
  showRationalizeTable = false;
  submittedWidgetIds = new Set<string>();
  dashboardNavItems: DashboardNavItem[] = [];
  private sideNavConfig: SideNavConfigItem[] = [];
  private isSideNavConfigLoaded = false;

  constructor(
    public configService: ConfigService,
    private route: ActivatedRoute,
    private router: Router,
    private globalFilterService: GlobalFilterService,
    private platformService: PlatformService,
    private http: HttpClient
  ) { }

  ngOnInit() {
    const routeDataSub = this.route.data.subscribe((data) => {
      const preserveQuoteFlowState = this.isQuotePartsFlowRoute(this.router.url);
      this.resolveDashboardNavItems();
      this.configurations = {};

      if (data['configFile']) {
        this.configFileName = data['configFile'];
        this.loadCharts();
      } else {
        const projectId = environment.PROJECT_ID;
        const appId = data['dvmWorkflowAppId'];
        if (appId) {
          this.loadDashboardFromApi(projectId, appId);
        }
      }
      if (!preserveQuoteFlowState) {
        this.globalFilterService.updateAllFilters({
          queryParams: {},
          requestParams: {},
          widgetId: null,
        });
        this.globalFilterService.activeFilter = null;
        this.globalFilterService.resetFilterSelection('customer_spec');
      }
    });
    this.subscriptions.add(routeDataSub);
  }

  private resolveDashboardNavItems(): void {
    const currentRoute = this.normalizePath(this.router.url);
    const applyDashboardNav = (config: SideNavConfigItem[]): void => {
      const parentSection = this.findTopLevelSectionForRoute(config, currentRoute);
      if (!parentSection) {
        this.dashboardNavItems = [];
        return;
      }

      const leafItems = this.collectLeafMenuItems(parentSection);
      this.dashboardNavItems = leafItems
        .filter((item) => !!item.name && !!this.normalizePath(item.path))
        .map((item) => ({
          label: item.name as string,
          route: this.normalizePath(item.path),
        }));
    };

    if (this.isSideNavConfigLoaded) {
      applyDashboardNav(this.sideNavConfig);
      return;
    }

    const sideNavSub = this.http
      .get<SideNavConfigItem[]>('assets/side-navbar-config/sidenav.config.json')
      .subscribe({
        next: (config) => {
          this.sideNavConfig = Array.isArray(config) ? config : [];
          this.isSideNavConfigLoaded = true;
          applyDashboardNav(this.sideNavConfig);
        },
        error: (err) => {
          console.error('Error loading side nav config:', err);
          this.dashboardNavItems = [];
          this.isSideNavConfigLoaded = true;
        },
      });
    this.subscriptions.add(sideNavSub);
  }

  private findTopLevelSectionForRoute(
    items: SideNavConfigItem[],
    currentRoute: string
  ): SideNavConfigItem | null {
    for (const item of items) {
      if (this.menuTreeContainsRoute(item, currentRoute)) {
        return item;
      }
    }
    return null;
  }

  private menuTreeContainsRoute(item: SideNavConfigItem, currentRoute: string): boolean {
    if (this.normalizePath(item.path) === currentRoute) {
      return true;
    }
    return !!item.children?.some((child) =>
      this.menuTreeContainsRoute(child, currentRoute)
    );
  }

  private collectLeafMenuItems(item: SideNavConfigItem): SideNavConfigItem[] {
    if (!item.children || item.children.length === 0) {
      return item.path ? [item] : [];
    }
    return item.children.flatMap((child) => this.collectLeafMenuItems(child));
  }

  private normalizePath(path?: string): string {
    if (!path) {
      return '';
    }
    const cleanPath = path.split('?')[0].split('#')[0].trim();
    if (!cleanPath) {
      return '';
    }
    return cleanPath.startsWith('/') ? cleanPath : `/${cleanPath}`;
  }

  private isQuotePartsFlowRoute(url?: string): boolean {
    const normalizedPath = this.normalizePath(url);
    return normalizedPath.includes('quote-parts');
  }

  get isQuoteAddScreen(): boolean {
    return this.normalizePath(this.router.url) === '/quote-parts';
  }

  get useCompactQuoteScale(): boolean {
    return this.normalizePath(this.router.url) === '/quote-parts-2';
  }

  onTableSubmitted(event: { widgetId: string }) {
    if (event?.widgetId) {
      this.submittedWidgetIds.add(event.widgetId);
    }
  }

  loadDashboardFromApi(projectId: string, appId: string) {
    const configSub = this.platformService
      .getDashboardConfigFromApi(projectId, appId)
      .subscribe({
        next: (response) => {
          //  API returns an array like:
          if (response?.length) {
            this.configurations = response[0].dvm_workflow_map;
          }
        },
        error: (err) => console.error('Error loading dashboard config:', err),
      });

    this.subscriptions.add(configSub);
  }
  loadCharts() {
    // Capture navigation state BEFORE the async config load
    const navState = window.history.state;
    console.log('[Dashboard] Navigation state:', navState);

    const configSub = this.configService
      .getDashboardConfig(this.configFileName)
      .subscribe((response) => {
        this.configurations = response;

        // Use captured navigation state
        if (navState?.submittedData && navState?.targetWidgetId) {
          const state = navState;
          // Map the submitted data to the target widget
          this.configurations.pageContent.forEach((row: any) => {
            row.columns.forEach((col: any) => {
              if (col.widgetId === state.targetWidgetId) {
                if (col.payload && col.payload.requestData) {
                  col.payload.requestData = { ...col.payload.requestData, ...state.submittedData };
                }
                if (!col.filters) col.filters = {};
                col.filters = { ...col.filters, ...state.submittedData };
              }
            });
          });

          // Set customerName initial value on the filter config (row data uses customer_name with underscore)
          const customerNameValue = state.submittedData?.customer_name || state.submittedData?.customerName || '';
          if (customerNameValue && this.configurations?.filters?.content) {
            const customerNameFilter = this.configurations.filters.content.find((f: any) => f.key === 'customerName');
            if (customerNameFilter) {
              customerNameFilter.initialValue = customerNameValue;
              console.log('[Dashboard] Set customerName initialValue:', customerNameValue);
            }
          }
        }
      });
    this.subscriptions.add(configSub);
  }

  ngOnDestroy() {
    this.subscriptions.unsubscribe();
    if (!this.isQuotePartsFlowRoute(this.router.url)) {
      this.globalFilterService.clearAllFilters();
      this.globalFilterService.activeFilter = null;
    }
  }

  onEditRecord(data: { record: any, config: any }) {
    this.editRecordData = data.record;
    this.editConfig = data.config;
    this.showEditDialog = true;
  }

  closeEditDialog() {
    this.showEditDialog = false;
    this.editRecordData = null;
    this.editConfig = null;
  }

  objectKeys(obj: any): string[] {
    return Object.keys(obj);
  }

  saveEdit() {
    if (!this.editConfig?.saveApiUrl) {
      console.error('Save API URL not configured');
      return;
    }

    // Replace placeholders in the URL
    const apiUrl = this.editConfig.saveApiUrl.replace('${API_BASE_URL}', environment.API_BASE_URL);

    const payload = {
      "requestData": {
        "source": "UI",
        "quote_details": [this.editRecordData]
      },
      "responseList": {},
      "entity": "quote_details",
      "workflowId": "QUOTE_DETAILS_WORKFLOW_001",
      "endpoints": [
        {
          "functionName": "QuoteDetailsInsertUpdateService",
          "version": "1.0"
        }
      ],
      "communicationType": "rest",
      "version": "1.0"
    };

    this.http.post(apiUrl, payload).subscribe({
      next: (response) => {
        console.log('Save successful:', response);
        // Optionally refresh the table or show success message
        this.closeEditDialog();
      },
      error: (error) => {
        console.error('Save failed:', error);
        // Optionally show error message
      }
    });
  }
}
